import { z } from 'zod'
import { paxtonHighlightAreaSchema } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import type { AnalyticsReference } from '@/analytics/schemas/analytics-reference.schemas'

/**
 * Content Viewer Session Types
 *
 * Documents describe what is rendered, contexts describe where it was opened,
 * and presentation carries viewer-only state such as highlights or default tabs.
 */

/**
 * Render Format
 *
 * Defines HOW a document should be rendered.
 */
export enum RenderFormat {
  HTML = 'html',
  PDF = 'pdf',
  MARKDOWN = 'markdown', // Render by base HTML
}
export const RenderFormatSchema = z.nativeEnum(RenderFormat)

export enum ViewerDocumentKind {
  ARTIFACT = 'ARTIFACT',
  LEGACY_FILE = 'LEGACY_FILE',
  USER_FILE = 'USER_FILE',
  CASELAW = 'CASELAW',
  LRR = 'LRR',
  WRITING_SAMPLE = 'WRITING_SAMPLE',
  MED_SUMMARY_CONVERTED_PDF = 'MED_SUMMARY_CONVERTED_PDF',
}

export enum ViewerOpenContextKind {
  CHAT_ARTIFACT = 'CHAT_ARTIFACT',
  CHAT_CITATION = 'CHAT_CITATION',
  MED_CHRON_REFERENCE = 'MED_CHRON_REFERENCE',
  MED_CHRON_SOURCE = 'MED_CHRON_SOURCE',
  MED_SUMMARY_REFERENCE = 'MED_SUMMARY_REFERENCE',
  MED_SUMMARY_SOURCE = 'MED_SUMMARY_SOURCE',
  TIMELINE_REFERENCE = 'TIMELINE_REFERENCE',
  TIMELINE_SOURCE = 'TIMELINE_SOURCE',
  DRIVE_FILE = 'DRIVE_FILE',
  SELECTED_FILE = 'SELECTED_FILE',
  AUTO_OPEN = 'AUTO_OPEN',
}

export enum ViewerDownloadTargetKind {
  ARTIFACT_DOCX = 'ARTIFACT_DOCX',
  LEGACY_OWNER_FILE = 'LEGACY_OWNER_FILE',
  USER_FILE_ORIGINAL = 'USER_FILE_ORIGINAL',
  RICH_REFERENCE_DOCX = 'RICH_REFERENCE_DOCX',
  WRITING_SAMPLE_PDF = 'WRITING_SAMPLE_PDF',
}

export type ViewerPdfRenderAsset = {
  format: RenderFormat.PDF
  filePath: string
  gcsBucketRef: string
  legacyAlwaysCloud: boolean
}

export type ViewerMarkdownRenderAsset = {
  format: RenderFormat.MARKDOWN
  filePath: string
  gcsBucketRef: string
  legacyAlwaysCloud: boolean
}

export type ViewerUserFileRenderAsset = {
  format: RenderFormat.PDF | RenderFormat.MARKDOWN
}

export type ViewerHtmlRenderAsset = {
  format: RenderFormat.HTML
  sourceKind: ViewerDocumentKind.CASELAW | ViewerDocumentKind.LRR
  storagePath: string
}

export type ViewerDownloadTarget =
  | {
      kind: ViewerDownloadTargetKind.ARTIFACT_DOCX
      artifactVersionId: string
    }
  | {
      kind: ViewerDownloadTargetKind.LEGACY_OWNER_FILE
      filePath: string
    }
  | {
      kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL
      userFileId: string
    }
  | {
      kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX
      sourceKind: ViewerDocumentKind.CASELAW | ViewerDocumentKind.LRR
      storagePath: string
      fileName: string
    }
  | {
      kind: ViewerDownloadTargetKind.WRITING_SAMPLE_PDF
      referenceId: string
      matterId: string
      fileName: string
    }

export type ArtifactViewerDocument = {
  kind: ViewerDocumentKind.ARTIFACT
  artifactVersionId: string
  name: string | null
  downloadTarget?: ViewerDownloadTarget
}

export type LegacyFileViewerDocument = {
  kind: ViewerDocumentKind.LEGACY_FILE
  id: string
  title: string
  renderAsset: ViewerPdfRenderAsset | ViewerMarkdownRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

export type UserFileViewerDocument = {
  kind: ViewerDocumentKind.USER_FILE
  userFileId: string
  title: string
  renderAsset: ViewerUserFileRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

export type CaselawViewerDocument = {
  kind: ViewerDocumentKind.CASELAW
  s3Dir?: string | null
  s3Path: string
  parentId: string
  title: string
  caseStatus?: string | null
  renderAsset: ViewerHtmlRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

export type LrrViewerDocument = {
  kind: ViewerDocumentKind.LRR
  s3Path: string
  title: string
  renderAsset: ViewerHtmlRenderAsset
  downloadTarget?: ViewerDownloadTarget
}

export type WritingSampleViewerDocument = {
  kind: ViewerDocumentKind.WRITING_SAMPLE
  referenceId: string
  title: string
  downloadTarget?: ViewerDownloadTarget
}

// A billing-summary source file's converted PDF (AI-94): non-PDF sources are extracted
// against a stored conversion, and citation highlight geometry is relative to it. The
// renderer resolves a short-lived signed URL by (assetId, userFileId) at render time.
export type MedSummaryConvertedPdfViewerDocument = {
  kind: ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF
  assetId: string
  userFileId: string
  title: string
  downloadTarget?: ViewerDownloadTarget
}

export type ViewerDocument =
  | ArtifactViewerDocument
  | LegacyFileViewerDocument
  | UserFileViewerDocument
  | CaselawViewerDocument
  | LrrViewerDocument
  | WritingSampleViewerDocument
  | MedSummaryConvertedPdfViewerDocument

export type ViewerOpenContext =
  | {
      kind: ViewerOpenContextKind.CHAT_ARTIFACT
      artifactVersionId: string
      conversationId: string
    }
  | {
      kind: ViewerOpenContextKind.CHAT_CITATION
      agentReferenceId: string
      citationId: string
      turnId: string
      conversationId: string
      analyticsReference?: AnalyticsReference
      highlightIndex?: number
    }
  | {
      kind: ViewerOpenContextKind.MED_CHRON_REFERENCE
      referenceId: string
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.MED_CHRON_SOURCE
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE
      referenceId: string
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.TIMELINE_REFERENCE
      referenceId: string
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.TIMELINE_SOURCE
      sourceId: string
      jobId?: string
    }
  | {
      kind: ViewerOpenContextKind.DRIVE_FILE
      matterId?: string
    }
  | {
      kind: ViewerOpenContextKind.SELECTED_FILE
      conversationId?: string
    }
  | {
      kind: ViewerOpenContextKind.AUTO_OPEN
      artifactVersionId?: string
      conversationId?: string
    }

export type ViewerPresentation = {
  pdfHighlights?: PaxtonHighlightArea[]
  markdownHighlights?: string[]
  startingPage?: number
  defaultTab?: CaseLawReferenceTab
}

export type ContentViewerSession = {
  document: ViewerDocument
  context: ViewerOpenContext
  presentation?: ViewerPresentation
}

/**
 * File Content Renderer Configuration
 *
 * Simplified discriminated union for file rendering.
 * The format field determines which renderer to use and which options are available.
 *
 * Key simplifications:
 * - GCS bucket is computed at render time from format (not stored)
 * - Options are flattened (no wrapper objects)
 * - Optional fields have sensible defaults
 *
 * Type-safe rendering where:
 * - PDF files get PDF-specific options (highlight areas, starting page)
 * - Markdown files get Markdown-specific options (sentence highlights)
 */
export const FileContentRendererConfigSchema = z.discriminatedUnion('format', [
  z.object({
    format: z.literal(RenderFormat.PDF),
    filePath: z.string(),
    highlights: z.array(paxtonHighlightAreaSchema).optional(),
    startingPage: z.number().optional(),
  }),
  z.object({
    format: z.literal(RenderFormat.MARKDOWN),
    filePath: z.string(),
    highlights: z.array(z.string()).nullable().optional(),
  }),
])
export type FileContentRendererConfig = z.infer<typeof FileContentRendererConfigSchema>
